<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const name = ref('');
const periodType = ref('monthly');
const periodStart = ref('');
const periodEnd = ref('');
const targetValue = ref('');
const targetDeals = ref('');
const targetAppointments = ref('');
const userId = ref('');
const funnelId = ref('');
const isActive = ref(true);
const notifyAt50 = ref(true);
const notifyAt80 = ref(true);
const notifyAt100 = ref(true);

const uiFlags = computed(() => store.getters['salesGoals/getUIFlags']);
const agents = useMapGetter('agents/getAgents');
const funnels = useMapGetter('funnels/getFunnels');

const periodOptions = [
  { value: 'weekly', label: 'SALES_GOALS_MGMT.FORM.PERIOD_TYPE.WEEKLY' },
  { value: 'monthly', label: 'SALES_GOALS_MGMT.FORM.PERIOD_TYPE.MONTHLY' },
  { value: 'quarterly', label: 'SALES_GOALS_MGMT.FORM.PERIOD_TYPE.QUARTERLY' },
  { value: 'yearly', label: 'SALES_GOALS_MGMT.FORM.PERIOD_TYPE.YEARLY' },
  { value: 'custom', label: 'SALES_GOALS_MGMT.FORM.PERIOD_TYPE.CUSTOM' },
];

const nameError = computed(() => {
  if (!name.value) return '';
  if (name.value.length < 2) {
    return t('SALES_GOALS_MGMT.FORM.NAME.MINIMUM_LENGTH_ERROR');
  }
  return '';
});

const isFormValid = computed(() => {
  return (
    name.value.length >= 2 &&
    periodStart.value &&
    periodEnd.value &&
    (parseFloat(targetValue.value) > 0 ||
      parseInt(targetDeals.value, 10) > 0 ||
      parseInt(targetAppointments.value, 10) > 0)
  );
});

const setPeriodDates = () => {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);

  switch (periodType.value) {
    case 'weekly':
      start.setDate(today.getDate() - today.getDay());
      end.setDate(start.getDate() + 6);
      break;
    case 'monthly':
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      break;
    case 'quarterly': {
      const quarter = Math.floor(today.getMonth() / 3);
      start.setMonth(quarter * 3, 1);
      end.setMonth(quarter * 3 + 3, 0);
      break;
    }
    case 'yearly':
      start.setMonth(0, 1);
      end.setMonth(11, 31);
      break;
    default:
      return;
  }

  periodStart.value = start.toISOString().split('T')[0];
  periodEnd.value = end.toISOString().split('T')[0];
};

const onClose = () => {
  emit('close');
};

const addSalesGoal = async () => {
  if (!isFormValid.value) return;

  try {
    await store.dispatch('salesGoals/create', {
      name: name.value,
      period_type: periodType.value,
      period_start: periodStart.value,
      period_end: periodEnd.value,
      target_value: parseFloat(targetValue.value) || 0,
      target_deals: parseInt(targetDeals.value, 10) || 0,
      target_appointments: parseInt(targetAppointments.value, 10) || 0,
      user_id: userId.value || null,
      funnel_id: funnelId.value || null,
      is_active: isActive.value,
      notify_at_50: notifyAt50.value,
      notify_at_80: notifyAt80.value,
      notify_at_100: notifyAt100.value,
    });
    useAlert(t('SALES_GOALS_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    const errorMessage =
      error.message || t('SALES_GOALS_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('funnels/get');
  setPeriodDates();
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('SALES_GOALS_MGMT.ADD.TITLE')"
      :header-content="$t('SALES_GOALS_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addSalesGoal">
      <woot-input
        v-model="name"
        class="w-full"
        :label="$t('SALES_GOALS_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('SALES_GOALS_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="nameError"
      />

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ $t('SALES_GOALS_MGMT.FORM.PERIOD_TYPE.LABEL') }}
        </label>
        <div class="relative w-full">
          <select
            v-model="periodType"
            class="w-full appearance-none ltr:pr-9 rtl:pl-9 px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-2 focus:ring-n-brand focus:outline-none"
            @change="setPeriodDates"
          >
            <option
              v-for="option in periodOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ $t(option.label) }}
            </option>
          </select>
          <span
            class="absolute -translate-y-1/2 pointer-events-none top-1/2 ltr:right-3 rtl:left-3 i-lucide-chevron-down size-4 text-n-slate-10"
          />
        </div>
      </div>

      <div class="flex gap-4 w-full mb-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('SALES_GOALS_MGMT.FORM.PERIOD_START.LABEL') }}
          </label>
          <input
            v-model="periodStart"
            type="date"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-2 focus:ring-n-brand focus:outline-none"
          />
        </div>
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('SALES_GOALS_MGMT.FORM.PERIOD_END.LABEL') }}
          </label>
          <input
            v-model="periodEnd"
            type="date"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-2 focus:ring-n-brand focus:outline-none"
          />
        </div>
      </div>

      <woot-input
        v-model="targetAppointments"
        class="w-full"
        type="number"
        min="0"
        :label="$t('SALES_GOALS_MGMT.FORM.TARGET_APPOINTMENTS.LABEL')"
        :placeholder="
          $t('SALES_GOALS_MGMT.FORM.TARGET_APPOINTMENTS.PLACEHOLDER')
        "
      />

      <div class="flex gap-4 w-full mb-4">
        <div class="flex-1">
          <woot-input
            v-model="targetValue"
            type="number"
            :label="$t('SALES_GOALS_MGMT.FORM.TARGET_VALUE.LABEL')"
            :placeholder="$t('SALES_GOALS_MGMT.FORM.TARGET_VALUE.PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11 -mt-2">
            {{ $t('SALES_GOALS_MGMT.FORM.TARGET_VALUE.HELP') }}
          </p>
        </div>
        <div class="flex-1">
          <woot-input
            v-model="targetDeals"
            type="number"
            :label="$t('SALES_GOALS_MGMT.FORM.TARGET_DEALS.LABEL')"
            :placeholder="$t('SALES_GOALS_MGMT.FORM.TARGET_DEALS.PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11 -mt-2">
            {{ $t('SALES_GOALS_MGMT.FORM.TARGET_DEALS.HELP') }}
          </p>
        </div>
      </div>

      <div class="flex gap-4 w-full mb-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('SALES_GOALS_MGMT.FORM.USER.LABEL') }}
          </label>
          <div class="relative w-full">
            <select
              v-model="userId"
              class="w-full appearance-none ltr:pr-9 rtl:pl-9 px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-2 focus:ring-n-brand focus:outline-none"
            >
              <option value="">
                {{ $t('SALES_GOALS_MGMT.FORM.USER.PLACEHOLDER') }}
              </option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
            <span
              class="absolute -translate-y-1/2 pointer-events-none top-1/2 ltr:right-3 rtl:left-3 i-lucide-chevron-down size-4 text-n-slate-10"
            />
          </div>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ $t('SALES_GOALS_MGMT.FORM.USER.HELP') }}
          </p>
        </div>
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('SALES_GOALS_MGMT.FORM.FUNNEL.LABEL') }}
          </label>
          <div class="relative w-full">
            <select
              v-model="funnelId"
              class="w-full appearance-none ltr:pr-9 rtl:pl-9 px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-2 focus:ring-n-brand focus:outline-none"
            >
              <option value="">
                {{ $t('SALES_GOALS_MGMT.FORM.FUNNEL.PLACEHOLDER') }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
            <span
              class="absolute -translate-y-1/2 pointer-events-none top-1/2 ltr:right-3 rtl:left-3 i-lucide-chevron-down size-4 text-n-slate-10"
            />
          </div>
        </div>
      </div>

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('SALES_GOALS_MGMT.FORM.NOTIFICATIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="notifyAt50" type="checkbox" class="w-4 h-4" />
            {{ $t('SALES_GOALS_MGMT.FORM.NOTIFICATIONS.AT_50') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="notifyAt80" type="checkbox" class="w-4 h-4" />
            {{ $t('SALES_GOALS_MGMT.FORM.NOTIFICATIONS.AT_80') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="notifyAt100" type="checkbox" class="w-4 h-4" />
            {{ $t('SALES_GOALS_MGMT.FORM.NOTIFICATIONS.AT_100') }}
          </label>
        </div>
      </div>

      <div class="flex items-center w-full gap-2 mb-4">
        <input
          id="is_active"
          v-model="isActive"
          type="checkbox"
          class="w-4 h-4"
        />
        <label for="is_active" class="text-sm text-n-slate-12">
          {{ $t('SALES_GOALS_MGMT.FORM.IS_ACTIVE.LABEL') }}
        </label>
      </div>
      <p class="text-xs text-n-slate-11 mb-4 -mt-2">
        {{ $t('SALES_GOALS_MGMT.FORM.IS_ACTIVE.HELP') }}
      </p>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('SALES_GOALS_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <Button
          type="submit"
          :label="$t('SALES_GOALS_MGMT.FORM.CREATE')"
          :disabled="!isFormValid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
